<template>
    <div>
        <app-bar :title="externalContent.name"/>

        <v-layout>
            <v-progress-linear
                v-if="overlay"
                absolute
                indeterminate
            ></v-progress-linear>

            <div class="pt-8 pb-12 px-8">
                <div class="pb-10">
                    <v-btn
                        @click="edit"
                        class="sm mr-5"
                        color="primary"
                    >{{ t('general_action_edit') }}
                    </v-btn>
                    <v-btn
                        class="sm mr-5"
                        color="primary"
                        outlined
                        @click.prevent="suspend(externalContent)"
                    >
                        {{ externalContent.published ? t('general_action_suspend') : t('general_action_unsuspend') }}
                    </v-btn>
                    <confirm-modal
                        :title="t('general_title_confirm_action')"
                        @agree="del(externalContent)"
                    >
                        <template #default="{ on, attrs }">
                            <v-btn
                                class="sm"
                                color="orange"
                                outlined
                                v-on="on"
                                v-bind="attrs"
                            >{{ t('general_action_delete') }}
                            </v-btn>
                        </template>
                    </confirm-modal>
                </div>
                <div class="float-left mr-10" style="width: 394px; max-width: 394px">
                    <div class="form-subtitle">{{ t('external_content_label_description') }}:</div>
                    <div class="form-subtitle-description" v-html="externalContent.description"></div>
                </div>
                <div class="float-left" style="max-width: calc(100% - 434px)">
                    <card-unit :items="items()">
                        <template #title>{{ t('external_content_label_id') }}: {{ externalContent.id }}</template>
                    </card-unit>
                </div>
            </div>
        </v-layout>
    </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, reactive, toRefs } from '@vue/composition-api'
import { useI18n } from 'vue-i18n-composable'
import { useStore } from '@/store'
import { useRouter } from '@/composition-api'
import { useEnums } from '@/core/defaults'
import { ExternalContent } from '@/core/models/external-content'
import moment from 'moment/moment'
import ConfirmModal from '@/components/ConfirmModal.vue'
import CardUnit from '@/components/CardUnit.vue'

export default defineComponent({
    components: {
        ConfirmModal,
        CardUnit
    },

    /**
     * Component constructor.
     *
     * @param props
     */
    setup(props) {
        const store = useStore()
        const router = useRouter()
        const i18n = useI18n()
        const enums = useEnums()
        const id = router.currentRoute.params['id']

        const state = reactive({
            overlay: true,
            currentTab: <number | null>null,
        })

        const capture = () => {
            store.dispatch('externalContent/get', {
                id: id,
                payload: { published: '' }
            }).then(() => {
                state.overlay = false
            })
        }

        const externalContentModel = computed(() => <ExternalContent>store.getters['externalContent/get'])

        const suspend = (externalContentModel: ExternalContent) => {
            state.overlay = true
            store.dispatch('externalContent/update', {
                id: externalContentModel.id,
                payload: { published: ! externalContentModel.published }
            }).then(() => {
                store.dispatch('notify', {color: 'success', message: i18n.t('general_notice_data_updated')})
                capture()
            })
        }

        const edit = () => router.push({
            name: 'external-contents.show',
            params: { id: <any>externalContentModel.value.id }
        })

        const del = (externalContentModel: ExternalContent) => {
            state.overlay = true
            store.dispatch('externalContent/delete', { id: externalContentModel.id }).then(() => {
                router.push({ name: 'external-contents' })
            })
        }

        const items = () => {
            let items = <any>[
                {
                    text: i18n.t('general_label_creation_date'),
                    value: externalContentModel.value.createdAt.format('DD/MM/YYYY')
                },
                {
                    text: i18n.t('external_content_label_type'),
                    value: enums.externalContentTypeTitle(externalContentModel.value.type)
                },
                { text: i18n.t('external_content_label_sort_order'), value: externalContentModel.value.sortOrder },
                {
                    text: i18n.t('external_content_label_published'),
                    value: externalContentModel.value.published ? i18n.t('general_text_yes') : i18n.t('general_text_no')
                },
            ]
            return items
        }

        onMounted(() => {
            capture()
        })

        return {
            capture,
            items,
            externalContent: externalContentModel.value,
            moment,
            edit,
            del,
            suspend,
            ...useI18n(),
            ...useEnums(),
            ...toRefs(state)
        }
    }
})
</script>
